<template>
    <button
        class="p-0 w-[10%] border-none relative"
        @mouseenter="() => (isHover = isEnableRow)"
        @mouseleave="() => (isHover = false)"
        @click="onItemClick"
    >
        <div
            class="stairItem"
            :class="[
                isOutOfRange ? 'hidden' : '',
                isEnableRow && !['', 'lose'].includes(round.status) ? 'bg-[#297fe5]' : 'bg-[#173760]'
            ]"
            :style="{
                boxShadow: isEnableRow && !['', 'lose'].includes(round.status) ? `0 4px 21px #2281f687` : '',
                cursor: isEnableRow && !['', 'lose'].includes(round.status) ? 'pointer' : 'auto'
            }"
        ></div>

        <div class="stair" :class="[(isShowStair || isHover) ? '' : 'hidden']"></div>

        <div v-if="isShowStone" class="stone"></div>
    </button>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import { roundData } from '../data';

const isHover = ref(false);

const props = defineProps({
    row: {
        type: Object as PropType<{ length; offset }>,
        default: 0,
        required: true,
    },
    rowIndex: {
        type: Number,
        required: true,
    },
    index: {
        type: Number,
        required: true,
    },
    round: {
        type: Object as PropType<typeof roundData>,
        required: true,
    },
    betIslocked: {
        type: Boolean,
        required: true,
    },
    itemClick: {
        type: Function,
        required: false,
    },
    isGameContent: {
        type: Boolean,
        default: false,
    },
    stairRows: {
        type: Array,
        required: true,
    },
});

const onItemClick = () => {
    if (isOutOfRange.value || !isEnableRow.value) return;

    props.itemClick(props.index);
};

const isOutOfRange = computed(() => {
    const { offset, length } = props.row;
    return props.index < offset || props.index > length + offset - 1;
});

const isEnableRow = computed(() => {
    if (props.isGameContent) return false;
    if (
        props.betIslocked
        && props.round.revealed.length === 0
        && props.rowIndex === 0
    ) {
        return true;
    }
    // ↓ 已经登上的楼层数组，长度2表示已经爬到了2楼，等待用户选择第3楼的落脚点 
    const field = (props.round?.field || []).filter(cur => {
        return cur?.length;
    });
    return (
        props.rowIndex === field.length
        && !['lose', ''].includes(props.round.status)
    );
});

/**
 * 是否显示石头
 */
const isShowStone = computed(() => {
    if (!props.round || !props.round.field || !Array.isArray(props.round.field[props.rowIndex])) {
        return false; // Handle the case where props.round or props.round.fields is undefined, or the array at props.round.fields[props.rowIndex] is not an array
    }
    const addOffset = (props.stairRows as any)[props.rowIndex].offset;

    return props.round.field[props.rowIndex].map((v) => v + addOffset).includes(props.index);
});
/**
 * 是否显示梯子
 */
const isShowStair = computed(() => {
    const addOffset = (props.stairRows as any)[props.rowIndex].offset;
    return props.round.revealed[props.rowIndex] === props.index - addOffset;
});
</script>

<style lang="scss" scoped>
.stairItem {
    border-radius: 4px;
    height: 12px;
    margin-left: 1px;
    margin-right: 1px;
    position: relative;
    transition: all 0.3s;
    z-index: 2;
    @media (max-width: 500px) {
        border-radius: 2px;
        height: 10px;
    }
}

.stair {
    background-image: url('../img/stair.svg');
    background-position: 50%;
    background-size: 20px 40px;
    display: block;
    height: 40px;
    left: 50%;

    position: absolute;
    top: -4px;
    transform: translate(-50%);
    width: 20px;
    z-index: 2;
    @media (max-width: 500px) {
        background-size: 13px 26px;
        height: 26px;
        width: 13px;
    }
}

.stone {
    animation: stoneAnim 0.25s ease-in 0s 1 forwards;
    background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACgAAAAoCAMAAAC7IEhfAAAB7FBMVEUAAAC4jGpYQC1iRjNGRkZdRDJdRDBgRTWYaz6jgGRZQS9ZQC6zjG/VtIlYQC5YPy65lHOmg2dZQC5jSTZaQTBZQC1aQS9XPy1aQjGmgmeqh2laQzBfQTHVt4mQb1dmTDjRroZ7XkrLqIGqhmvDn3xhSDbRr4VwVEHJpYCthmzSsIhwVUFeRDOUdV6zjG5ZQi/CnXnUsoizjG/UtIi0i3BmTDpeRTLUsohYQC90WUSzjnFYQC7UtIiMbVayjXCZeGB0WESzjm9aQzGwjW7WtIm1jXBrUTtXQS7VtIiFaFOzjXKQc13Fn32Qbl1QQDBmVUSLdF3VtImzjG/FnH93W0aTdF3TsofRsIbDmn3JpYC0jXCwim2ngmiLbVW3kXKXd1+RcltaQS9XPy3QroXAmHu/mnixjG+sh2uffWR4XEfNq4PHpH7CmXy2kHKJa1SDZU90WURmTDjSsIfPrITOrITLqYLEoHy7lna1jnGaeGCVdl2TclmNb1h/YUxyV0JsUT1jSjfKqIDGon7DnXu/l3i7k3a4k3OsjGyviGypiGmhfmWcfGF6XklgRjPKpoHGn37Am3m5knS0j3GgfWKPcFeHalJ2WkVvVD9tUj5qTzteRDKzk3GrhmmnhGh8X0q/n3q4mHO0k3KBZE57X0plqvlBAAAAUXRSTlMAEeMXA3s+IAX++u/n393Mwru5tbGrlYNiWk9MLyH+/fj39fTv7Ovm4t7c29jRxcK7tra0sKyjn5+el5eOhYJ/f3Vua2NgX15cVkM8NR4QDwv7MqifAAADCElEQVQ4y52TZ1saQRSFx66xxRpbeu+9997LLjNKB+mgiIo0AUUpIooxdo2a9kczdxZ2Fx+TD55v89x3zp1zZwbtVUVPr9XwVJqzDY/f/4d7WZOKq0bMXp8/xacdFx9W7ObV8fzRpYCKy0vl5u2GyN2KndgNtT4Q93Iy9eh5TYYcLSvg3p3wdouESC7w/JbR2VQpcR9OycrSDj9NpY2MXSgR+9aKNXMg2T+XX3QHKTnjWW0uzYEPJL9pt0KhFD3nKbgVwbHcOQ9ykma+KRQKcdUHEyWzg9VC89syMEwdv4qrCZ4q5MFDB+SGTFOhzYURjhtxsfY+AB06vFoNp7xeOBOKcTZ7MuGF1gxcwiumcoResHzWRNwl2dosQiQrgAYjxrH7qLKWhQ0qFcr89f0IQSKFihvWszAE46HL6A2z0CZpRTmXS65NAOgXOmsJceL1YnQVSjqe9hLjhm1KWMU5ZmgAx/F96Bxch45XgPoBg20M7BOiEELPuNaLjsCLCqsFcJiBS2kLLLx6AEcJiTAQsk4sCaBFAKdGaRjlpB+4NCFkgLU+BrPz8P0MFC65e3rR5p7vYYZ2CkYxhDkDpWV7Ujoj7Owzcyo+BxqddDz16AobY1ithG6cTAEGallnvNGC7jGH5cVgwuKWc91q4DYNxDiL8ZjpNWo3s2cyGl785ZKDZurmoDMkHkw7m0pQkb/gQYga5m0EpMPUsLcRIbRfrH0PWuakTbwGuIgTOFMXBdt9udJPrfzN+pijzonHsiYTe7df8pZVGngxsu8yCoEHe02mVuFzdUzmOhs01mHZ7+cNEHi8t7Uc5dTgygX1ScNJ0dmwCcbuIFEfj0+wqoSp3OxSojDqTiTpbZXVJRCTVr1aHVTz+S+A14orkUxPDmkXAlZrihKiMuQ3RHmGCvTqtGdZRzJpEdNkYNQr2Xq0Q587m84PbntCdobZtHQ0TjyYrStBu6jr5jj+MxByOGaIUReFyTR/Qrur/Nbh1ZXZaHSbuq1n69pK0T9VUdZ4MjY0FNsobmlDe9JfDXSmw7ntXKcAAAAASUVORK5CYII=);
    background-position: 50%;
    background-repeat: no-repeat;
    background-size: 20px 20px;
    display: block;
    height: 20px;
    left: 0;
    margin-left: auto;
    margin-right: auto;
    opacity: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: -200px;
    width: 20px;
    z-index: 3;
    will-change: top;
    @media (max-width: 500px) {
        animation: stone-mobile .25s ease-in 0s 1 forwards;
        background-size: 11px 11px;
        height: 11px;
        width: 11px;
    }
}

@keyframes stoneAnim {
    0% {
        opacity: 0;
        top: -200px;
    }

    100% {
        opacity: 1;
        top: -19px;
    }
}
@keyframes stone-mobile {
    0% {
        opacity: 0;
        top: -200px;
    }

    100% {
        opacity: 1;
        top: -10px;
    }
}
</style>
